import { z } from 'zod';
import type { AccessLevel } from './constants';

// ---------- DTOs ----------

export type DocParentType = 'workspace' | 'space' | 'folder' | 'list';
export const DOC_PAGE_WIDTHS = ['normal', 'wide', 'full'] as const;
export type DocPageWidth = (typeof DOC_PAGE_WIDTHS)[number];

export interface DocDTO {
  id: string;
  /** Where the doc lives. `workspace` docs belong to no location. */
  parentType: DocParentType;
  parentId: string;
  title: string;
  icon: string | null;
  cover: string | null;
  isPrivate: boolean;
  /** Token of the public read-only link, or null when the link is off. */
  publicToken: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** The caller's access level on the doc. */
  access: AccessLevel;
  /** First page in tree order, to open by default. Null only while a doc has no pages. */
  firstPageId: string | null;
  pageCount: number;
}

export interface DocPageDTO {
  id: string;
  docId: string;
  parentPageId: string | null;
  title: string;
  icon: string | null;
  cover: string | null;
  position: string;
  /** Locked pages are read-only for everyone until unlocked. */
  locked: boolean;
  width: DocPageWidth;
  updatedAt: number;
  updatedBy: string | null;
}

export interface DocDetailDTO {
  doc: DocDTO;
  pages: DocPageDTO[];
}

export interface DocVersionDTO {
  id: string;
  pageId: string;
  /** Name given to a version saved by hand. */
  label: string | null;
  /** Null for versions saved automatically. */
  createdBy: string | null;
  createdAt: number;
}

/** A doc opened through its public link: no ids of people, no access level. */
export interface PublicDocDTO {
  title: string;
  icon: string | null;
  cover: string | null;
  pages: Pick<DocPageDTO, 'id' | 'parentPageId' | 'title' | 'icon' | 'cover' | 'position' | 'width'>[];
}

/** Page content as a Yjs state update, base64 encoded (version previews, public pages, exports). */
export interface DocSnapshotDTO {
  pageId: string;
  snapshot: string;
}

/** Realtime payload of `doc.changed`: refetch the doc (or the doc list when it was added or removed). */
export interface DocChangedEventData {
  docId: string;
  op: 'created' | 'updated' | 'deleted' | 'restored' | 'pages';
  pageId?: string;
}

// ---------- Live editing protocol ----------

/**
 * Message types on the page socket (`/ws/doc/:workspaceId/:pageId`). Every frame is binary and
 * starts with one of these as a varint, followed by a y-protocols sync or awareness payload.
 */
export const DOC_MESSAGE = { sync: 0, awareness: 1, queryAwareness: 3 } as const;

/** Close code sent when access changed (lock, sharing, deletion): the client should reconnect or leave. */
export const DOC_CLOSE_RELOAD = 4001;

/** The Yjs XML fragment the editor binds to. */
export const DOC_FRAGMENT = 'default';

// ---------- Schemas ----------

const id = z.string().min(1).max(64);
const title = z.string().trim().max(200);
const icon = z.string().max(16).nullable();
/** A colour, gradient name or a same-origin file URL. */
const cover = z.string().max(300).nullable();

export const docQuerySchema = z.object({
  parentType: z.enum(['workspace', 'space', 'folder', 'list']).optional(),
  parentId: id.optional(),
});

export const createDocSchema = z.object({
  parentType: z.enum(['workspace', 'space', 'folder', 'list']).default('workspace'),
  /** Required unless the doc is created at workspace level. */
  parentId: id.optional(),
  title: title.optional(),
  isPrivate: z.boolean().optional(),
});
export type CreateDocInput = z.infer<typeof createDocSchema>;

export const updateDocSchema = z.object({
  title: title.min(1).optional(),
  icon: icon.optional(),
  cover: cover.optional(),
  isPrivate: z.boolean().optional(),
  /** Move the doc to another location (give both), or to workspace level. */
  parentType: z.enum(['workspace', 'space', 'folder', 'list']).optional(),
  parentId: id.optional(),
  /** Turns the public read-only link on or off. */
  isPublic: z.boolean().optional(),
});
export type UpdateDocInput = z.infer<typeof updateDocSchema>;

export const createDocPageSchema = z.object({
  parentPageId: id.nullable().optional(),
  title: title.optional(),
  /** Fractional position among siblings. Defaults to the end. */
  position: z.string().max(64).optional(),
});
export type CreateDocPageInput = z.infer<typeof createDocPageSchema>;

export const updateDocPageSchema = z.object({
  title: title.optional(),
  icon: icon.optional(),
  cover: cover.optional(),
  position: z.string().max(64).optional(),
  /** Reparent; null makes it a top level page. */
  parentPageId: id.nullable().optional(),
  locked: z.boolean().optional(),
  width: z.enum(DOC_PAGE_WIDTHS).optional(),
});
export type UpdateDocPageInput = z.infer<typeof updateDocPageSchema>;

export const createDocVersionSchema = z.object({ label: z.string().trim().max(120).optional() });

export const docMentionSchema = z.object({ userIds: z.array(id).min(1).max(20) });

// ---------- Helpers ----------

/** Pages as a depth-first list in tree order, each with its depth. Orphans are treated as top level. */
export function flattenDocPages<P extends Pick<DocPageDTO, 'id' | 'parentPageId' | 'position'>>(
  pages: P[],
): { page: P; depth: number }[] {
  const ids = new Set(pages.map((p) => p.id));
  const children = new Map<string | null, P[]>();
  for (const page of pages) {
    const parent = page.parentPageId && ids.has(page.parentPageId) ? page.parentPageId : null;
    const list = children.get(parent);
    if (list) list.push(page);
    else children.set(parent, [page]);
  }
  for (const list of children.values()) list.sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
  const out: { page: P; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (parent: string | null, depth: number) => {
    for (const page of children.get(parent) ?? []) {
      if (seen.has(page.id)) continue;
      seen.add(page.id);
      out.push({ page, depth });
      visit(page.id, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Standard base64, written out by hand so it works the same in the browser, the Worker and tests. */
export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2]! + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    out += b === undefined ? '=' : B64[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    out += c === undefined ? '=' : B64[c & 63]!;
  }
  return out;
}

export function base64ToBytes(text: string): Uint8Array {
  const clean = text.replace(/[^A-Za-z0-9+/]/g, '');
  const bytes = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let n = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const a = B64.indexOf(clean[i]!);
    const b = B64.indexOf(clean[i + 1] ?? 'A');
    const c = clean[i + 2] === undefined ? -1 : B64.indexOf(clean[i + 2]!);
    const d = clean[i + 3] === undefined ? -1 : B64.indexOf(clean[i + 3]!);
    bytes[n++] = (a << 2) | (b >> 4);
    if (c >= 0) bytes[n++] = ((b & 15) << 4) | (c >> 2);
    if (d >= 0) bytes[n++] = ((c & 3) << 6) | d;
  }
  return bytes.subarray(0, n);
}
